
import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import Button from '../../../../../components/ComponentsV1/Button';
import InputV1 from '../../../../../components/ComponentsV1/InputV1';
import { useClient } from '../../../../../api/api-client';
import {
  IVA_POLICIES_ENDPOINTS,
  LOOKUP_TYPE,
  LookupTypeOptions,
  LOOKUP_TYPE_ID_LABEL,
  LOOKUP_TYPE_ID_PLACEHOLDER,
  ROLLOUT_GROUP,
  RolloutGroupOptions,
  inputClassName,
} from '../constants';
import { toQuery, readErrorMessage } from '../helpers';
import { useComplaintTypes } from '../useComplaintTypes';
import IvaSkeleton from './IvaSkeleton';
import ConfirmModal from './ConfirmModal';
import GuidelinesDrawer from './GuidelinesDrawer';

const VersionCard = ({ query, version, onRespawned }) => {
  const client = useClient();
  const updatedBy = useSelector(
    (state) => state.authState?.userDetails?.crm_user_id,
  );
  const [target, setTarget] = useState(ROLLOUT_GROUP.TARGET);
  const [isRespawning, setIsRespawning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const versionLabel = version.rollout_group || version.version;

  const handleRespawn = useCallback(async () => {
    setShowConfirm(false);
    setIsRespawning(true);
    try {
      await client(IVA_POLICIES_ENDPOINTS.RESPAWN, {
        data: {
          complaint_type: query.complaintType.trim(),
          lookup_type: query.lookupType,
          lookup_id: query.lookupId.trim(),
          version: versionLabel,
          target_rollout_group: target,
          updated_by: String(updatedBy || ''),
        },
      });
      toast.success(`Restored ${versionLabel} to ${target}`);
      if (onRespawned) onRespawned();
    } catch (error) {
      toast.error(readErrorMessage(error));
    } finally {
      setIsRespawning(false);
    }
  }, [client, query, versionLabel, target, onRespawned]);

  return (
    <>
      <div className='tw-bg-white tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-flex tw-items-center tw-gap-4 tw-px-6 tw-py-5'>
        {/* Version badge */}
        <div className='tw-flex-1 tw-min-w-0'>
          <span className='tw-inline-flex tw-items-center tw-gap-2 tw-bg-grey-100 tw-border tw-border-solid tw-border-grey-200 tw-rounded-lg tw-px-4 tw-py-2'>
            <span className='tw-w-2 tw-h-2 tw-rounded-full tw-bg-base-green tw-shrink-0' />
            <span className='tw-text-[1.4rem] tw-font-semibold tw-text-grey-900 tw-font-mono'>
              {versionLabel}
            </span>
          </span>
        </div>

        {/* Controls */}
        <div className='tw-flex tw-items-center tw-gap-3 tw-flex-wrap tw-justify-end tw-shrink-0'>
          <div className='tw-flex tw-items-center tw-gap-2'>
            <span className='tw-text-[1.3rem] tw-text-grey-500 tw-whitespace-nowrap'>
              Restore to
            </span>
            <div className='tw-w-[180px]'>
              <InputV1
                options={RolloutGroupOptions}
                value={target}
                placeholder='Select group'
                isSearchable
                closeOnSelect
                onSelect={(opt) => setTarget(opt?.value)}
              />
            </div>
          </div>
          <Button
            title='Read Guidelines'
            type='secondary'
            onClick={() => setShowDrawer(true)}
          />
          <Button
            title='Respawn'
            onClick={() => setShowConfirm(true)}
            disabled={isRespawning}
            loading={isRespawning}
          />
        </div>
      </div>

      {showDrawer && (
        <GuidelinesDrawer
          title={versionLabel}
          sections={[
            {
              label: 'Summary Guidelines',
              steps: version.media_summarization_guidelines,
            },
            {
              label: 'Verification Guidelines',
              steps: version.step_by_step_verification_guidelines,
            },
          ]}
          emptyText='No guidelines recorded for this version.'
          onClose={() => setShowDrawer(false)}
        />
      )}

      {showConfirm && (
        <ConfirmModal
          title='Respawn Version'
          message={`This will restore version "${versionLabel}" to ${target}, overwriting the current data. Are you sure?`}
          onConfirm={handleRespawn}
          onCancel={() => setShowConfirm(false)}
          loading={isRespawning}
        />
      )}
    </>
  );
};

/* ─── VersionsSection ─── */

const VersionsSection = () => {
  const client = useClient();
  const [complaintType, setComplaintType] = useState('');
  const [lookupType, setLookupType] = useState(LOOKUP_TYPE.CATEGORY);
  const [lookupId, setLookupId] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { options: complaintTypeOptions, isLoading: metadataLoading } =
    useComplaintTypes();

  const handleComplaintTypeChange = useCallback((opt) => {
    setComplaintType(opt?.value || '');
    setResult(null);
  }, []);

  const handleLookupTypeChange = useCallback((opt) => {
    setLookupType(opt?.value || LOOKUP_TYPE.CATEGORY);
    setLookupId('');
    setResult(null);
  }, []);

  const fetchVersions = useCallback(async () => {
    if (!complaintType.trim() || !lookupId.trim()) {
      toast.error('Complaint type and lookup id are required');
      return;
    }
    setIsLoading(true);
    try {
      const response = await client(
        `${IVA_POLICIES_ENDPOINTS.VERSIONS}${toQuery({
          complaint_type: complaintType,
          lookup_type: lookupType,
          lookup_id: lookupId,
        })}`,
      );
      setResult(response || {});
    } catch (error) {
      setResult(null);
      toast.error(readErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [client, complaintType, lookupType, lookupId]);

  const versions = Array.isArray(result?.versions) ? result.versions : [];

  const idLabel = LOOKUP_TYPE_ID_LABEL[lookupType] || 'Lookup Id';
  const idPlaceholder = LOOKUP_TYPE_ID_PLACEHOLDER[lookupType] || 'e.g. 8058';

  return (
    <div className='tw-flex tw-flex-col tw-gap-6'>
      <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-flex tw-flex-col tw-gap-4'>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
            Complaint Type<span className='tw-text-red-500'>*</span>
          </label>
          <InputV1
            loading={metadataLoading}
            options={complaintTypeOptions}
            placeholder='Choose complaint type'
            multiple={false}
            isSearchable
            closeOnSelect
            onSelect={handleComplaintTypeChange}
            value={complaintType}
            style={{ width: '100%', display: 'block' }}
          />
        </div>
        <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6'>
          <div className='tw-flex tw-flex-col tw-gap-2'>
            <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
              Lookup Type
            </label>
            <InputV1
              options={LookupTypeOptions}
              value={lookupType}
              placeholder='Select lookup type'
              isSearchable
              closeOnSelect
              onSelect={handleLookupTypeChange}
              style={{ width: '100%', display: 'block' }}
            />
          </div>
          <div className='tw-flex tw-flex-col tw-gap-2'>
            <label className='tw-text-[1.4rem] tw-font-medium tw-text-grey-800'>
              {idLabel}<span className='tw-text-red-500'>*</span>
            </label>
            <input
              className={inputClassName}
              placeholder={idPlaceholder}
              value={lookupId}
              autoComplete='off'
              onChange={(e) => setLookupId(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Button
            title='Fetch Versions'
            onClick={fetchVersions}
            disabled={isLoading}
            loading={isLoading}
          />
        </div>
      </div>

      {isLoading && <IvaSkeleton />}

      {!isLoading && result && versions.length === 0 && (
        <div className='tw-bg-white tw-p-8 tw-rounded-2xl tw-border tw-border-solid tw-border-grey-200 tw-text-[1.4rem] tw-text-grey-500'>
          No archived versions found for this lookup.
        </div>
      )}

      {!isLoading &&
        versions.map((version, idx) => (
          <VersionCard
            key={version.rollout_group || version.version || idx}
            query={{ complaintType, lookupType, lookupId }}
            version={version}
            onRespawned={fetchVersions}
          />
        ))}
    </div>
  );
};

export default VersionsSection;
